/**
 * Tela onde o administrador vê quem tem conta e libera módulos.
 *
 * Papel: junta a busca, a `TabelaUsuarios` paginada pela API e a caixa de
 * diálogo com a `GradePermissoes` da pessoa escolhida.
 *
 * Como usar: a rota desta página deverá ficar dentro da `RotaDeAdministrador` no
 * `router.tsx`. Fora dela a página abriria para qualquer autenticado e mostraria
 * só o erro 403 da API — o dado continuaria protegido, mas a pessoa veria uma
 * tela quebrada em vez do aviso de acesso restrito.
 *
 * Quem aparece aqui é quem já entrou no Concierge ao menos uma vez: a linha em
 * `usuarios` nasce na primeira consulta a `/api/sessao`. Para liberar acesso a
 * alguém novo, a pessoa precisa entrar primeiro, e depois ser encontrada aqui.
 */
import { type Usuario } from '@concierge/shared';
import { type ChangeEvent, useEffect, useState } from 'react';

import { ErroDaApi } from '@/shared/api';
import { Button } from '@/shared/ui/button';
import { CabecalhoDoModulo } from '@/shared/ui/cabecalho-do-modulo';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { GradePermissoes } from '../components/grade-permissoes';
import { TabelaUsuarios } from '../components/tabela-usuarios';
import { useUsuarios } from '../hooks/usar-usuarios';

/** Quantas pessoas cada página traz. */
const POR_PAGINA = 50;

/** Quanto esperar depois da última tecla antes de buscar, em milissegundos. */
const ESPERA_DA_BUSCA = 300;

/**
 * Página de usuários e permissões.
 *
 * A busca só vai à API depois de uma pausa na digitação, e volta para a
 * primeira página a cada mudança: continuar na página três de uma busca nova
 * mostraria uma lista vazia para um termo que tem resultados.
 *
 * @returns A tela com busca, tabela, paginação e a caixa da grade.
 */
export function PaginaUsuarios() {
  const [pagina, setPagina] = useState(1);
  const [textoDaBusca, setTextoDaBusca] = useState('');
  const [busca, setBusca] = useState('');
  const [selecionado, setSelecionado] = useState<Usuario | null>(null);

  useEffect(() => {
    // A página volta a 1 junto com a busca, e não a cada tecla: voltar antes
    // dispararia uma consulta da página 1 com o termo antigo, que ninguém pediu.
    const espera = setTimeout(() => {
      setBusca(textoDaBusca.trim());
      setPagina(1);
    }, ESPERA_DA_BUSCA);
    return () => clearTimeout(espera);
  }, [textoDaBusca]);

  const consulta = useUsuarios({
    pagina,
    porPagina: POR_PAGINA,
    busca: busca === '' ? undefined : busca,
  });

  const total = consulta.data?.total ?? 0;
  const ultimaPagina = Math.max(1, Math.ceil(total / POR_PAGINA));
  const primeiroDaPagina = total === 0 ? 0 : (pagina - 1) * POR_PAGINA + 1;
  const ultimoDaPagina = Math.min(pagina * POR_PAGINA, total);

  /** Guarda o texto digitado; a consulta espera a pausa na digitação. */
  function handleBuscaChange(evento: ChangeEvent<HTMLInputElement>) {
    setTextoDaBusca(evento.target.value);
  }

  /** Abre a caixa com a grade da pessoa da linha. */
  function handleEditar(usuario: Usuario) {
    setSelecionado(usuario);
  }

  /** Fecha a caixa, seja pelo botão, pelo `Esc` ou por clique no véu em volta dela. */
  function handlePainelOpenChange(aberto: boolean) {
    if (!aberto) setSelecionado(null);
  }

  /** Fecha a caixa sem gravar. */
  function handleFecharPainel() {
    setSelecionado(null);
  }

  /** Refaz a consulta depois de uma falha. */
  function handleTentarDeNovo() {
    void consulta.refetch();
  }

  return (
    <div>
      <CabecalhoDoModulo
        titulo="Usuários e permissões"
        subtitulo="QUEM TEM CONTA E OS MÓDULOS QUE PODE ABRIR"
      />

      <div className="mb-4 flex max-w-sm flex-col gap-2">
        <Label htmlFor="busca-usuarios">Buscar por nome ou e-mail</Label>
        <Input
          id="busca-usuarios"
          type="search"
          value={textoDaBusca}
          onChange={handleBuscaChange}
          autoComplete="off"
        />
      </div>

      {consulta.error ? (
        <div className="rounded-md border border-dashed border-border bg-card px-6 py-12 text-center">
          <p className="font-serif text-lg text-foreground">
            Não foi possível carregar os usuários
          </p>
          <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
            {consulta.error instanceof ErroDaApi
              ? consulta.error.message
              : 'A consulta falhou antes de chegar ao servidor.'}
          </p>
          <Button
            variant="outline"
            className="mt-4"
            disabled={consulta.isFetching}
            onClick={handleTentarDeNovo}
          >
            {consulta.isFetching ? 'Tentando…' : 'Tentar de novo'}
          </Button>
        </div>
      ) : (
        <>
          <TabelaUsuarios
            usuarios={consulta.data?.itens}
            carregando={consulta.isPending}
            mensagemVazia={
              busca === ''
                ? 'Ninguém entrou ainda. A pessoa aparece aqui depois de abrir a aplicação pela primeira vez.'
                : `Ninguém encontrado para “${busca}”.`
            }
            onEditar={handleEditar}
          />

          <div className="mt-3 flex flex-wrap items-center justify-between gap-3 text-sm text-muted-foreground">
            <p className="tabular" aria-live="polite">
              {total === 0 ? '' : `${primeiroDaPagina}–${ultimoDaPagina} de ${total}`}
              {consulta.isPlaceholderData && ' · atualizando…'}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={pagina <= 1 || consulta.isPlaceholderData}
                onClick={() => setPagina((atual) => atual - 1)}
              >
                Anterior
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={pagina >= ultimaPagina || consulta.isPlaceholderData}
                onClick={() => setPagina((atual) => atual + 1)}
              >
                Próxima
              </Button>
            </div>
          </div>
        </>
      )}

      <Dialog open={selecionado !== null} onOpenChange={handlePainelOpenChange}>
        <DialogContent>
          {selecionado !== null && (
            <>
              <DialogHeader>
                <DialogTitle>
                  {selecionado.nome ?? selecionado.email ?? 'Sem nome no Auth0'}
                </DialogTitle>
                <DialogDescription>
                  {selecionado.email ?? selecionado.auth0Sub} · o que esta pessoa pode abrir
                </DialogDescription>
              </DialogHeader>
              <GradePermissoes
                key={selecionado.id}
                usuario={selecionado}
                onSalvo={handleFecharPainel}
                onCancelar={handleFecharPainel}
              />
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
